/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	Component,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	ViewChild,
	OnInit,
	ElementRef,
} from '@angular/core';

import { TicketEditService } from '../../ticket-edit.service';
import { PlanState } from 'src/app/services/plan-state.service';
import { UntypedFormBuilder } from '@angular/forms';

import { TicketEditComponent, setupFormForConstraint } from '../ticket-edit.component';
import { ConstraintAcceptanceService } from '../../constraint-acceptance.service';
import { CONSTRAINT_ACCEPTANCE_STATE } from '@touchplan/common';
import { PlanAccessCheck } from 'src/app/services/PlanAccessCheck';
import { takeUntil, filter } from 'rxjs/operators';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import * as appStrings from '@touchplan/strings';
import {
	DateAdapter,
	MAT_DATE_FORMATS,
	MAT_NATIVE_DATE_FORMATS,
	NativeDateAdapter,
} from '@angular/material/core';

@Component({
	selector: 'constraint-edit',
	templateUrl: './constraint-edit.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	// Use the native date adapter here vs our custom Iso8601DateAdapter
	// (Won't be refactored yet since this will be replaced by Docked ticket editing.)
	providers: [
		{ provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS },
		{ provide: DateAdapter, useClass: NativeDateAdapter },
	],
})
export class ConstraintEditComponent extends TicketEditComponent implements OnInit {
	private isFromActualizingState = false;

	public strings = appStrings;

	@ViewChild('scrollToBottom') private scrollToBottom: ElementRef | undefined;

	constructor(
		public override ticketEditService: TicketEditService,
		protected override formBuilder: UntypedFormBuilder,
		public override planState: PlanState,
		protected override cd: ChangeDetectorRef,
		public override constraintAcceptanceService: ConstraintAcceptanceService,
		protected override planAccessCheck: PlanAccessCheck,
		protected override featureFlagManager: FeatureFlagManager
	) {
		super(
			ticketEditService,
			formBuilder,
			planState,
			cd,
			constraintAcceptanceService,
			planAccessCheck,
			featureFlagManager
		);
		setupFormForConstraint(this.editForm);
	}

	public override ngOnInit() {
		super.ngOnInit();
		// MOC-4314 - Open 'More Details' after user selects 'Saves' after actualizing a ticket
		this.calendarController$
			.pipe(
				filter((thing) => thing),
				takeUntil(this.shutdownSubject)
			)
			.subscribe(() => {
				this.isFromActualizingState = true;
			});
	}

	public requestedByChanged(newRequestedByProjectMemberId: string): void {
		this.constraintAcceptanceService.setRequestedByProjectMemberId(this.data, newRequestedByProjectMemberId);
	}

	public assignedToSelected() {
		if (this.scrollToBottom) {
			this.scrollToBottom.nativeElement.scrollTop = this.scrollToBottom.nativeElement.scrollHeight;
		}
	}

	public footerSaveClicked(): void {
		// MOC-3259 - Special handling for constraint logic (Help bar)
		// TP-391 - Should not be able to auto-accept constraint if constraint is not in plan
		if (this.isAcceptableConstraint && this.isAssignedTo && this.data.ticketsInPlanRegion) {
			this.ticketEditService.markFormAsDirty();
			this.constraintAcceptanceService.setAccepted(this.data);
			this.save();
		} else if (this.isFromActualizingState) {
			this.ticketEditService.openMoreDetailsFromEdit(this.data);
			this.saveCustomFields();
		} else {
			this.save();
		}
	}

	get isAcceptableConstraint(): boolean {
		return (
			(this.data.data.constraintState === CONSTRAINT_ACCEPTANCE_STATE.ASSIGNED ||
				this.data.data.constraintState === CONSTRAINT_ACCEPTANCE_STATE.UNASSIGNED) &&
			this.isAssignedTo
		);
	}

	get isSaveEnabled(): boolean {
		const isPlanRegion: boolean = this.data.ticketsInPlanRegion;
		return this.isAcceptableConstraint && isPlanRegion && !this.planAccessCheck.hasRestrictedAccessSync
			? true
			: this.editForm.dirty;
	}

	get isAssignedTo(): boolean {
		if (this.data.data.liveAssignedProjectMemberId) {
			return this.planAccessCheck.pmMatchesSync(this.data.data.liveAssignedProjectMemberId);
		} else {
			return false;
		}
	}

	get footerLabelName(): string {
		return this.isAcceptableConstraint &&
			this.data.ticketsInPlanRegion &&
			!this.planAccessCheck.hasRestrictedAccessSync
			? 'Accept'
			: 'Save';
	}
}
